import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Save } from "lucide-react";

const FIELDS = [
  { key: "business_name", label: "Business name", placeholder: "Sun Fabricators" },
  { key: "owner_name", label: "Owner name", placeholder: "Ramesh Patil" },
  { key: "what_we_sell", label: "What we sell", placeholder: "ACP shopfronts, signage & fabrication" },
  { key: "key_offers", label: "Key offers", placeholder: "10% off installation before Diwali" },
  { key: "service_area", label: "Service area", placeholder: "Navi Mumbai, Thane, Vashi" },
  { key: "typical_customer", label: "Typical customer", placeholder: "Retail shop owners, restaurants" },
  { key: "tone_notes", label: "Tone notes", placeholder: "Warm, first-name basis, Hinglish OK" },
  { key: "phone_signature", label: "Phone signature", placeholder: "— Ramesh, Sun Fabricators" },
  { key: "typical_deal_size", label: "Typical deal size (₹)", placeholder: "e.g. 40000 — used by Missed Money Finder for value estimates" },
  { key: "autopilot_instructions", label: "Autopilot standing instructions", placeholder: "Never message the 'wholesale' tag; always propose re-engagement for 60-day-quiet warm leads." },
];

export default function BusinessProfileCard() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    api.get("/business-profile").then((r) => setProfile(r.data)).catch(() => setProfile({}));
  }, []);

  if (!profile) {
    return (
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-6 text-sm text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading business profile…
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business-profile", profile);
      toast.success("Business profile saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const autofill = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/business-profile/autofill");
      setProfile((p) => ({ ...p, ...r.data }));
      toast.success("Draft ready. Review each field before saving.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fill failed.");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-6" data-testid="business-profile-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400">// Autopilot Fuel</div>
          <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1">Business profile</h2>
          <p className="text-xs text-zinc-500 mt-1">Powers per-recipient personalization and the autopilot planner.</p>
        </div>
        <button
          onClick={autofill}
          disabled={drafting}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
          data-testid="business-profile-autofill-btn"
        >
          {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Auto-fill from my data
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.key === "autopilot_instructions" || f.key === "what_we_sell" || f.key === "key_offers" ? "md:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{f.label}</label>
            {f.key === "autopilot_instructions" || f.key === "tone_notes" || f.key === "what_we_sell" || f.key === "key_offers" ? (
              <textarea
                data-testid={`business-profile-${f.key}`}
                value={profile[f.key] || ""}
                onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                rows={3}
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
              />
            ) : (
              <input
                data-testid={`business-profile-${f.key}`}
                value={profile[f.key] || ""}
                onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/40"
              />
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
          data-testid="business-profile-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save profile
        </button>
      </div>
    </div>
  );
}
